import * as microsoftTeams from "@microsoft/teams-js";

export interface UserInfo {
  userId: string;
  displayName: string;
  email: string;
}

export interface PlatformContextInfo {
  teamId?: string;
  channelId?: string;
  chatId?: string;
  tenantId?: string;
  userId?: string;
  subEntityId?: string;
  isInChannel: boolean;
  isInChat: boolean;
}

export interface PlatformContext {
  readonly platform: "teams" | "web";
  initialize(): Promise<void>;
  getUserInfo(): Promise<UserInfo | null>;
  getAuthToken(): Promise<string | null>;
  getContext(): PlatformContextInfo | null;
  getContextId(): string;
}

// ============================================
// Teams Platform Context
// ============================================

class TeamsPlatformContext implements PlatformContext {
  readonly platform = "teams" as const;
  private context: PlatformContextInfo | null = null;
  private userInfo: UserInfo | null = null;
  private cachedToken: string | null = null;
  private tokenExpiry: number = 0;

  async initialize(): Promise<void> {
    // Teams SDK is already initialized in App.tsx
    const ctx = await microsoftTeams.app.getContext();

    const isInChannel = !!(ctx.team?.groupId && ctx.channel?.id);
    const isInChat = !!(ctx.chat?.id) && !isInChannel;

    this.context = {
      teamId: ctx.team?.groupId,
      channelId: ctx.channel?.id,
      chatId: ctx.chat?.id,
      tenantId: ctx.user?.tenant?.id,
      userId: ctx.user?.id,
      subEntityId: (ctx.page as any)?.subPageId,
      isInChannel,
      isInChat,
    };

    // Pre-fetch user info from token so email is available for contextId
    await this.getUserInfo();
  }

  async getUserInfo(): Promise<UserInfo | null> {
    if (this.userInfo) return this.userInfo;

    const token = await this.getAuthToken();
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      this.userInfo = {
        userId: (payload.preferred_username || payload.upn || payload.email || "").toLowerCase(),
        displayName: payload.name || "",
        email: (payload.preferred_username || payload.upn || payload.email || "").toLowerCase(),
      };
      return this.userInfo;
    } catch {
      return null;
    }
  }

  async getAuthToken(): Promise<string | null> {
    if (this.cachedToken && Date.now() < this.tokenExpiry) {
      return this.cachedToken;
    }

    try {
      const token = await microsoftTeams.authentication.getAuthToken();
      this.cachedToken = token;
      this.tokenExpiry = Date.now() + 50 * 60 * 1000;
      return token;
    } catch (err) {
      console.error("SSO getAuthToken failed:", err);
      return null;
    }
  }

  getContext(): PlatformContextInfo | null {
    return this.context;
  }

  getContextId(): string {
    if (!this.context) return "";

    if (this.context.isInChannel) {
      return `channel-${this.context.teamId}-${this.context.channelId}`.replace(/[^a-zA-Z0-9-]/g, "-");
    }
    if (this.context.isInChat) {
      return `chat-${this.context.chatId}`.replace(/[^a-zA-Z0-9-]/g, "-");
    }
    const email = this.userInfo?.email || this.context.userId || "";
    return `personal-${email}`.replace(/[^a-zA-Z0-9-_.@]/g, "-");
  }
}

// ============================================
// Web Platform Context
// ============================================

class WebPlatformContext implements PlatformContext {
  readonly platform = "web" as const;
  private userInfo: UserInfo | null = null;

  async initialize(): Promise<void> {
    // Load user from localStorage (set by WebAuthService)
    const stored = localStorage.getItem("gantt-web-user");
    if (stored) {
      const user = JSON.parse(stored);
      this.userInfo = {
        userId: user.email.toLowerCase(),
        displayName: user.displayName,
        email: user.email.toLowerCase(),
      };
    }
  }

  async getUserInfo(): Promise<UserInfo | null> {
    return this.userInfo;
  }

  async getAuthToken(): Promise<string | null> {
    return localStorage.getItem("gantt-auth-token");
  }

  getContext(): PlatformContextInfo | null {
    return {
      userId: this.userInfo?.userId,
      isInChannel: false,
      isInChat: false,
    };
  }

  getContextId(): string {
    // Use email so web and Teams personal share the same plans
    const stored = localStorage.getItem("gantt-web-user");
    if (stored) {
      const user = JSON.parse(stored);
      const email = (user.email || "").toLowerCase();
      if (email) return `personal-${email}`.replace(/[^a-zA-Z0-9-_.@]/g, "-");
      return `web-${user.id}`;
    }
    return "web-anonymous";
  }
}

// ============================================
// Factory
// ============================================

let instance: PlatformContext | null = null;

export function getPlatformContext(forceTeams?: boolean): PlatformContext {
  if (instance) return instance;

  if (forceTeams) {
    instance = new TeamsPlatformContext();
  } else {
    instance = new WebPlatformContext();
  }

  return instance;
}
